import React from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
  UseFormStateReturn,
} from "react-hook-form";
import { isEmpty, renderErrorMessages } from "@/main/services/utils";
import KogInputText from "../inputText/inputText";
import * as S from './styles';

type IConfigRange = {
  rangeNumbers: { min: number, max: number };
  decimalPlaces?: number;
}
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'number' | 'color' | 'size'>;
interface IKogInputPercentageControlledProps<T extends FieldValues> {
  idScreen: string;
  name: FieldPath<T>;
  label?: string;
  required?: boolean;
  placeholder?: string;
  configRange?: Partial<IConfigRange>
  customInputProps?: IHTMLInputProps;
  forceMin?: boolean;
  onChangeCustom?: (value: string) => void;
}
const INITIAL_CONFIG_RANGE: IConfigRange = {
  rangeNumbers: { min: 0, max: 100 },
  decimalPlaces: 2,
}
/**
 * Component to render a controlled input range
 *
 * @param {string} idScreen - Id of the screen
 * @param {string} name - Name of input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @param {Bool} forceMin - Force the min value
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <KogInputPercentageControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
*/
function KogInputPercentageControlled<TFieldValues extends FieldValues>({
  idScreen,
  name,
  label,
  placeholder,
  control,
  required = false,
  forceMin = false,
  configRange: configRangeProps = {},
  customInputProps: customInputPropsPROP = {},
  onChangeCustom = () => { },
}: Required<Pick<UseControllerProps<TFieldValues>, 'control'>> & IKogInputPercentageControlledProps<TFieldValues>) {
  const configRange = { ...INITIAL_CONFIG_RANGE, ...configRangeProps };
  const customInputProps = { ...customInputPropsPROP, required };

  function maskPercentage(value: string, oldValue: string) {
    const separator = value.includes(',') ? ',' : '.';
    const newValue = value.replace(/[^0-9,.]/g, '');
    const valueNumber = parseFloat(newValue);
    const { rangeNumbers, decimalPlaces } = configRange;

    // #region Validations
    const objValidateValue = {
      exceedMax: valueNumber > rangeNumbers.max,
      exceedMinAndOldValueIsMin: valueNumber < rangeNumbers.min && (oldValue === rangeNumbers.min.toString() || oldValue === ''),
      exceedMin: valueNumber < rangeNumbers.min,
      wrong2Separators: newValue.split(separator).length > 2,
      have2CommasDot: newValue.split(',').length > 1 && newValue.split('.').length > 1,
      wrongDecimalPlaces: decimalPlaces && newValue.split(separator)[1] && newValue.split(separator)[1].length > decimalPlaces,
    }
    const haveError = Object.values(objValidateValue).find((value) => value);
    if (objValidateValue.exceedMax) return rangeNumbers.max.toString();
    if (objValidateValue.exceedMinAndOldValueIsMin) return forceMin ? rangeNumbers.min.toString() : newValue;
    if (objValidateValue.exceedMin) return forceMin ? rangeNumbers.min.toString() : newValue;
    if (haveError) return oldValue;
    //#endregion

    return newValue;
  }

  const renderInput = ({ field, fieldState: { error } }: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
  }) => {
    const validateValue = field.value;
    const idScreenInput = `${idScreen}-txt-${field.name}`;

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const oldValue = field.value;
      const newValue = e.target.value;
      const validateValue = maskPercentage(newValue, oldValue);
      field.onChange(validateValue);
      onChangeCustom(validateValue);
    }

    const onKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const ignoreKeys = ['e', 'E', '+', '-', ','];
      if (ignoreKeys.includes(e.key)) {
        e.preventDefault();
      }
    }

    const baseInputProps: IHTMLInputProps = {
      ...field,
      ...customInputProps,
      id: idScreenInput,
      value: validateValue,
      onChange: onChangeInput,
      onKeyDown: onKeyDownInput
    }

    return (
      <S.InpuTWithSuffix>
        <KogInputText
          hasOnlyNumber
          autoComplete="off"
          type="number"
          label={label}
          forceShrink
          placeholder={placeholder}
          error={!!error}
          helperText={renderErrorMessages(label, error)}
          {...baseInputProps}
        />
      </S.InpuTWithSuffix>
    )
  }
  const VALIDATE_RANGE_DEFAULT: Record<string, (value: string) => boolean | string> = {
    min: (value: string) => {
      if (isEmpty(value)) return true;
      return Number(value) >= configRange.rangeNumbers.min
    },
    max: (value: string) => {
      if (isEmpty(value)) return true;
      return Number(value) <= configRange.rangeNumbers.max
    },
  }

  return (
    <S.Container >
      <Controller
        name={name}
        control={control as Control<FieldValues>}
        rules={{
          required,
          validate: VALIDATE_RANGE_DEFAULT,
        }}
        render={renderInput}
      />

    </S.Container >

  );
};

export default KogInputPercentageControlled;
